<script lang="ts" setup>
import type { EventDto } from '@/types/base'
import TheIconAndText from '@/components/atoms/TheIconAndText/TheIconAndText.vue'
import ThePill from '@/components/atoms/ThePill/ThePill.vue'
import GoogleMapsMapView from '@/components/organisms/GoogleMapsMapView/GoogleMapsMapView.vue'
import GoogleStreetView from '@/components/organisms/GoogleStreetView/GoogleStreetView.vue'
import { useTagStore } from '@/stores/tagStore'
import { storeToRefs } from 'pinia'
import { dateFormat, formatStartAndEndDate } from '@/utils'
import { computed, ref } from 'vue'
import { useEventTypeStore } from '@/stores/eventTypeStore'
import { useEventOrganizerStore } from '@/stores/eventOrganizer'
import { useNewEventStore } from '@/stores/newEventStore'
import { computedAsync } from '@vueuse/core'
const newEventStore = useNewEventStore()
//const { eventOrganizer, eventOrganizerType } = storeToRefs(newEventStore)
const eventTypeStore = useEventTypeStore()

const props = defineProps<{
    event?: EventDto
    imageUrl?: string
}>()

const eventType = computedAsync(async () => {
    return eventTypeStore.getEventType(props.event?.typeId as number)
})

const eventOrganizer = computedAsync(async () => {
    return useEventOrganizerStore().getEventOrganizerByIdAndPartitionKey(props.event?.creatorId as number, props.event?.typeId.toString() as string)
})

const organizations = ref<Record<string, string>>(
    { null: "Ingen", "dn": "Danmarks Naturfredningsforening", "sportsfiskerforbund": "Danmarks Sportsfiskerforbund", "jaegerforbund": "Danmarks Jægerforbund", "other": "Anden organisation, kommune eller andet" }
);

function getOrganizationValue(key: string): string {
  return organizations.value[key];
}

const _suitableFor = computed(
    () =>
        eventType.value?.sections
            .flatMap((x) => x.fields)
            ?.find((x) => x.type === 'suitableFor')?.values ?? {}
)

const suitableFor = computed(() => {
    return props.event?.suitableFor?.map((y) => _suitableFor.value[y])
})

const tagStore = useTagStore()
const { tags } = storeToRefs(tagStore)
const _tags = tags.value
const eventTags = computed(() => {
    return _tags.filter((x) => x.id && props.event?.tags?.includes(x.id))
})

const showContact = computedAsync(async () => {
  const eventType = await eventTypeStore.getEventType(props.event?.typeId as number);
  
  if (!eventType || !eventType.sections) {
    return false;
  }
  
  return eventType.sections.some(section =>
    section.fields.some(field => field.type === "contact")
  );
});

const isAffaldsindsamling = computed(() => {
    if (eventType.value) {
        return eventType.value.name.toLowerCase().includes('affaldsindsamling')
    }
    return false
})

function signupTypeMessage() {
    const signupType = props.event?.signupType
    if (signupType === 'link') {
        return '<a target="_blank" href="'+props.event?.signupTypeData+'">'+props.event?.signupTypeData+'</a>'
    } else if (signupType === 'none') {
        return 'Tilmelding ikke nødvendigt'
    } else if (signupType === 'sms') {
        return 'Tilmelding med SMS til nr:&nbsp;'+props.event?.signupTypeData
    } else if (signupType === 'email') {
        return `Tilmelding med email til:&nbsp;${props.event?.signupTypeData}`;
    }
    else if (signupType === 'phone') {
        return 'Tilmelding på telefon&nbsp;<a href="tel:'+props.event?.signupTypeData+'">'+props.event?.signupTypeData+'</a>'
    }else if (signupType === 'text') {
        return 'Tilmelding: '+props.event?.signupTypeData
    } else {
        return 'Tilmelding ikke nødvendigt'
    }
}
</script>
<template>
    <div
        v-if="event"
        class="event"
    >
        <div class="container">
            <div class="event__information">
                <p
                    v-if="event.start"
                    class="event__date"
                >
                    {{ dateFormat(event.start) }}
                </p>
                <div class="col-xs-12 col-xxl-9" v-if="event.title && event.title.length > 0">  
                    <h1
                        v-if="event.title && event.title.length > 0"
                        class="event__title"
                    >
                        {{ event.title }}
                    </h1>
                </div>
                <img
                    v-if="
                        (imageUrl && imageUrl.length > 0) ||
                        (event.image?.url && event.image.url.length > 0)
                    "
                    :alt="event.image?.altText"
                    :src="imageUrl || event.image?.url"
                    class="event__media event__information-media"
                />
                <div class="col-xs-12 col-xxl-8" v-if="event.description && event.description.length > 0 || event.teaser && event.teaser.length > 0">
                    <h2 class="event__sub-title">Beskrivelse</h2>
                    <p
                        v-if="event.teaser && event.teaser.length > 0"
                        class="event__teaser-text"
                    >
                        {{ event.teaser }}
                    </p>
                    <p
                        v-if="event.description && event.description.length > 0"
                        class="event__description" >
                        {{ event.description }}
                    </p>
                </div>
            </div>
            <div class="event__details">
                <h2 class="event__sub-title">Arrangement detaljer</h2>
                <div class="event__details-row row">
                    <div class="event__details-col col-xs-12 col-md-12">
                      
                        <div v-if="event.start && event.end" class="event__detail">
                            <p class="event__detail-label">Tid og dato</p>
                            <TheIconAndText
                                :text="formatStartAndEndDate(event.start, event.end)"
                                icon="calendar" />
                        </div>
                        <div v-if="event.address && !event.hideAddress"
                            class="event__detail" >
                            <p class="event__detail-label">Adresse</p>
                            <TheIconAndText
                                :text="`${event.address.streetName} ${event.address.houseNumber}, ${event.address.postalCode} ${event.address.city}`"
                                icon="mapPin" />
                        </div>
                        <div v-if="event.locationDirections" class="event__detail">
                            <p class="event__detail-label">Mødested</p>
                            <TheIconAndText
                                :text="event.locationDirections"
                                icon="compass" />
                                
                               
                        </div>
                        <div class="event__detail" v-if="!event.hideContact && event.contact && event.contact?.firstName && showContact">
                            <p class="event__detail-label">Kontakt information</p>
                         
                            <TheIconAndText
                                :text="`${event.contact?.firstName} ${event.contact?.lastName}<br>${event.contact?.emailAddress}, ${event.contact?.phoneNumber}`"
                                icon="users"/>
                        </div>

                        <div class="event__detail event-organizer" v-if="eventOrganizer != undefined" >
                            <p class="event__detail-label">Arrangør <span class="icon-and-text--secondary">({{ eventOrganizer?.name }})</span></p>
                        </div>
                 
                        <div
                            v-if="eventTags && eventTags.length > 0" class="event__detail" >
                            <p class="event__detail-label">Emneord og tags</p>
                            <div class="event__tags">
                                <ThePill v-for="tag in eventTags"
                                    :key="tag.id"
                                    :isSelected="true"
                                    :label="tag.name.toString()"
                                    :notChangeAble="true"
                                    class="filled" />
                            </div>
                        </div>
                        <div v-if="suitableFor && suitableFor.length > 0" class="event__detail" >
                            <p class="event__detail-label">Egnet for</p>
                            <div class="event__tags">
                                <ThePill
                                    v-for="sF in suitableFor"
                                    :key="sF"
                                    :isSelected="false"
                                    :label="sF"
                                    :notChangeAble="true"
                                    class="filled" />
                            </div>
                        </div>
                        <div class="event__detail" v-if="event.signupType">
                            <p class="event__detail-label">Tilmelding</p>
                            <TheIconAndText
                                :text="signupTypeMessage()"
                                :type="event.signupType != 'none' ? 'primary' : 'secondary'"
                                icon="users" />
                        </div>

                        <div class="event__detail" v-if="event.association">
                            <p class="event__detail-label">Forening</p>
                            <TheIconAndText
                                :text="getOrganizationValue(event.association)"
                                :type="event.signupType != 'none' ? 'primary' : 'secondary'"
                                icon="users" />
                        </div>


                        <div class="event__detail" v-if="event.expectedParticipants != undefined && event.expectedParticipants > 1">
                            <p class="event__detail-label">Forventede deltagere</p>
                            <TheIconAndText
                                :text="event.expectedParticipants?.toString() || 'Ikke angivet'"
                                type="primary"
                                icon="users" />
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="event.location?.latitude" class="event__location">
                <div class="event__detail">
                    <p class="event__detail-label">Kortvisning</p>
                    <GoogleMapsMapView
                        :disableSwitching="true"
                        :latitude="event.location?.latitude"
                        :longitude="event.location?.longitude"
                        :satellite="true" />
                </div>
                <div v-if="event.location.useStreetView" class="event__detail">
                    <p class="event__detail-label">Google Streetview</p>
                    <GoogleStreetView
                        :disableSwitching="true"
                        :heading="event.location.heading"
                        :latitude="event.location?.latitude"
                        :longitude="event.location?.longitude"
                        :pitch="event.location.pitch"
                        :prevent-movement="true"
                        :showStreetView="true"
                        :zoom="event.location.zoom" />
                </div>
            </div>
        </div>
    </div>
</template>
<style lang="scss" scoped>
.event {
    $event-border-bottom: 1px solid $dn-blue-light;

    padding-top: rem(34);

    &__sub-title {
        font-size: rem(24);
        font-weight: 700;
        margin-bottom: rem(24);
        color: $dn-blue;
    }

    &__teaser-text {
        @include font-size(20);
        font-weight: 700;
        margin-bottom: rem(24);
    }

    &__description,
    &__teaser-text {
        white-space: pre-line;
    }

    /* Information */
    &__information {
        padding-bottom: rem(20);
        border-bottom: $event-border-bottom;

        &-media {
            margin-bottom: rem(40);
        }
    }

    &__date {
        font-family: var(--font-serif, initial);
        font-size: rem(16);
        font-weight: 700;
    }

    &__title {
        font-size: rem(36);
        @include media-breakpoint-up(md) {
            font-size: rem(48);
        }
        font-weight: 700;
        margin-bottom: rem(20);
        color: $dn-blue;
    }

    &__media {
        width: 100%;
    }

    /* Details */
    &__details {
        padding: rem(50) 0;
       

        &-row {
            gap: rem(50);
            @include media-breakpoint-up(md) {
                gap: rem(0);
            }
        }

        &-col {
            display: flex;
            flex-direction: column;
            gap: rem(50);
        }
    }

    &__detail {
        width: 100%;

        &-label {
            font-size: rem(21);
            font-weight: 700;
            margin-top: 0;
            margin-bottom: rem(20);
        }
    }

    &__tags {
        display: flex;
        flex-wrap: wrap;
        gap: rem(16);
    }

    /* Location */
    &__location {
        border-top: $event-border-bottom;
        display: flex;
        flex-direction: column;
        padding-top: rem(50);
        padding-bottom: rem(90);
        gap: rem(44);

        @include media-breakpoint-up(md) {
            flex-direction: row;
        }
    }
}
.localhost,
.mineture-tidtiltur-dk {
    .event-organizer {
        display: none;
    }

}

</style>
